import { useState, type CSSProperties, type FormEvent } from 'react'

export const MIN_PASSWORD_LENGTH = 12 // must match backend routes/auth.js

export interface ChangePasswordResult {
  success: boolean
  error?: string
  /** The session re-issued on success — every earlier session, this one included, is revoked. */
  token?: string
}

interface Props {
  changePassword: (currentPassword: string, newPassword: string) => Promise<ChangePasswordResult>
  /** Called after a successful change, once the success message is set. */
  onChanged?: () => void
}

type Feedback = { kind: 'error' | 'success'; message: string } | null

const labelStyle: CSSProperties = {
  display: 'block', fontSize: '0.875rem', color: 'var(--ra-text-secondary, #a8b2cc)', marginBottom: '0.4rem',
}
const inputStyle: CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.75rem', borderRadius: 8,
  border: '1px solid var(--ra-glass-border, rgba(255,255,255,0.18))', background: 'rgba(255,255,255,0.04)',
  color: 'inherit', fontSize: '0.95rem',
}
// Status is carried by the leading word and icon, never by colour alone.
const feedbackStyle: CSSProperties = {
  padding: '0.65rem 0.75rem', borderRadius: 8, fontSize: '0.9rem', marginBottom: '1rem',
  border: '1px solid var(--ra-glass-border, rgba(255,255,255,0.18))',
}

export function ChangePasswordForm({ changePassword, onChanged }: Props) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [feedback, setFeedback] = useState<Feedback>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setFeedback(null)
    if (next.length < MIN_PASSWORD_LENGTH) {
      setFeedback({ kind: 'error', message: `New password must be at least ${MIN_PASSWORD_LENGTH} characters.` })
      return
    }
    if (next !== confirm) {
      setFeedback({ kind: 'error', message: 'New passwords do not match.' })
      return
    }
    setSubmitting(true)
    try {
      const result = await changePassword(current, next)
      if (result.success) {
        setCurrent('')
        setNext('')
        setConfirm('')
        setFeedback({ kind: 'success', message: 'Password changed. You are still signed in here; any other signed-in sessions have been signed out.' })
        onChanged?.()
      } else {
        setFeedback({ kind: 'error', message: result.error || 'Could not change your password.' })
      }
    } catch {
      setFeedback({ kind: 'error', message: 'Could not change your password. Check your connection and try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate style={{ maxWidth: 420 }}>
      {feedback?.kind === 'error' && (
        <div role="alert" style={feedbackStyle}>⚠ Error: {feedback.message}</div>
      )}
      {feedback?.kind === 'success' && (
        <div role="status" style={feedbackStyle}>✓ {feedback.message}</div>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <label htmlFor="cp-current" style={labelStyle}>Current password</label>
        <input id="cp-current" type="password" autoComplete="current-password" style={inputStyle}
          value={current} onChange={(e) => setCurrent(e.target.value)} />
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label htmlFor="cp-new" style={labelStyle}>New password</label>
        <input id="cp-new" type="password" autoComplete="new-password" style={inputStyle}
          aria-describedby="cp-new-hint" value={next} onChange={(e) => setNext(e.target.value)} />
        <div id="cp-new-hint" style={{ fontSize: '0.8rem', color: 'var(--ra-text-muted, #8892b0)', marginTop: '0.35rem' }}>
          At least {MIN_PASSWORD_LENGTH} characters. A passphrase works well.
        </div>
      </div>
      <div style={{ marginBottom: '1.25rem' }}>
        <label htmlFor="cp-confirm" style={labelStyle}>Confirm new password</label>
        <input id="cp-confirm" type="password" autoComplete="new-password" style={inputStyle}
          value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </div>

      <button type="submit" disabled={submitting} style={{
        padding: '0.65rem 1.25rem', borderRadius: 8, border: 'none', cursor: submitting ? 'wait' : 'pointer',
        background: 'var(--ra-cyber-aqua, #22d3ee)', color: '#0a192f', fontWeight: 600, fontSize: '0.95rem',
      }}>
        {submitting ? 'Changing…' : 'Change password'}
      </button>
    </form>
  )
}
